<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import {
  NForm,
  NFormItem,
  useMessage,
} from 'naive-ui'
import type {
  FormInst,
  FormRules,
  UploadFileInfo,
} from 'naive-ui'
import { AppInput, AppUpload, SvgIcon } from '@/components/common'
import { t } from '@/locales'
import { useUserStore } from '@/store'
import { createClassroom, updateClassroom } from '@/api/classroom'
import { getGradeList, getGradeSubjectList } from '@/api/classroomSystemSetting'
import { uploadFile } from '@/api/file'
import { transformMinioUrl } from '@/utils/functions'

const props = defineProps({
  dataSource: {
    type: Object,
    required: true,
    default: {},
  },
  title: {
    type: String,
    default: '',
  },
  show: {
    type: Boolean,
    default: false,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  isView: {
    type: Boolean,
    default: false,
  },
  isCreate: {
    type: Boolean,
    default: false,
  },
  showSubject: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['onClose'])
// const appStore = useAppStore()
const userStore = useUserStore()

const workSpaceId = computed(() => userStore.workSpaceId)
const showModal = ref(props.show)
const message = useMessage()
const formRef = ref<FormInst | null>(null)
const modelRef = ref<any>({
  name: null,
  description: null,
  class_block_code: null,
  subject_id: null,
  avatar_id: null,
  avatar_url: null,
  ...props.dataSource,
  class_block: props.dataSource?.class_code || null,
})
const rulesRef = ref<FormRules>({
  name: [
    {
      required: true,
      message: t('validation.required', { name: t('classroom.name') }),
      trigger: ['blur', 'input'],
    },
  ],
  class_block: [
    {
      required: true,
      message: t('validation.required', { name: t('classroom.classBlock') }),
      trigger: ['blur', 'change'],
    },
  ],
  subject_id: [],
  description: [],
})
const loading = ref<boolean>(false)
const listClassBlock = ref<any[]>([])
const listSubject = ref<any[]>([])

const uploadingAvatar = ref<boolean>(false)
const avatarPreviewUrl = ref<string>(modelRef.value.avatar_url ? transformMinioUrl(modelRef.value.avatar_url) : '')
const avatarFileList = ref<UploadFileInfo[]>([])

const computedAvatarSrc = computed(() => {
  if (avatarPreviewUrl.value)
    return avatarPreviewUrl.value
  if (modelRef.value.avatar_url)
    return transformMinioUrl(modelRef.value.avatar_url)
  return ''
})

const onBeforeUploadAvatar = async (options: { file: UploadFileInfo; fileList: Array<UploadFileInfo> }) => {
  if (props.isView)
    return

  const file = options?.file?.file as File | undefined
  if (!file)
    return

  try {
    uploadingAvatar.value = true

    // optimistic preview
    avatarPreviewUrl.value = URL.createObjectURL(file)

    const res = await uploadFile({
      file,
      data: {
        obj_type: 'CLASSROOM_AVATAR',
      },
    })

    if (res?.success) {
      const fileId = res?.data?.id
      const url = res?.data?.url

      modelRef.value.avatar_id = fileId || null
      modelRef.value.avatar_url = url || modelRef.value.avatar_url
      if (url)
        avatarPreviewUrl.value = transformMinioUrl(url)

      message.success(res?.message || '')
    }
    else {
      message.error(res?.message || 'Đã có lỗi xảy ra')
    }
  }
  catch (err) {
    message.error('Đã có lỗi xảy ra')
  }
  finally {
    uploadingAvatar.value = false
    // allow re-selecting the same file; we don't show list anyway
    avatarFileList.value = []
  }
}

const onUpdateAvatarFileList = (fileList: UploadFileInfo[]) => {
  avatarFileList.value = fileList
}

const closeModal = (reload: boolean, id?: any) => {
  showModal.value = false
  if (reload)
    emit('onClose', reload, id)
  else
    emit('onClose', reload)
}

const onCreate = async () => {
  try {
    loading.value = true
    const params = {
      ...modelRef.value,
      workspace_id: workSpaceId.value,
    }
    const res = await createClassroom(params)
    if (res && res.data && res.data.success) {
      message.success(res.data.message)
      closeModal(true, res.data.data.id)
    }
    else {
      message.error(res?.data?.message || 'Đã có lỗi xảy ra')
    }
  }
  catch {}
  finally {
    loading.value = false
  }
}

const onUpdate = async () => {
  try {
    loading.value = true
    const params = {
      ...modelRef.value,
      workspace_id: workSpaceId.value,
    }
    const res = await updateClassroom(params)
    if (res && res.data && res.data.success) {
      message.success(res.data.message)
      closeModal(true, res.data.data.id)
    }
    else {
      message.error(res?.data?.message || 'Đã có lỗi xảy ra')
    }
  }
  catch {}
  finally {
    loading.value = false
  }
}

const handleSave = () => {
  if (uploadingAvatar.value) {
    message.warning(t('common.loading'))
    return
  }

  formRef.value?.validate().then(async () => {
    if (props.isEdit)
      await onUpdate()
    else
      await onCreate()
  }).catch((e: any) => {
  }).finally(() => {})
}

const handleCancel = () => {
  closeModal(false)
}

const getListClassBlock = async () => {
  try {
    const params = {
      keyword: '',
      page: 1,
      page_size: 100,
      sort: true,
      sort_by: '',
    }
    const res = await getGradeList(params)
    if (res?.success)
      listClassBlock.value = res.data?.items || []
    else
      listClassBlock.value = []
  }
  catch {
    listClassBlock.value = []
  }
}

const getListSubject = async (classBlockCode: any) => {
  try {
    listSubject.value = []
    if (!classBlockCode)
      return

    const params = {
      grade_code: classBlockCode,
      subject_code: '',
      keyword: '',
      page: 1,
      page_size: 200,
      sort: true,
      sort_by: '',
    }

    const res = await getGradeSubjectList(params)
    if (res?.success) {
      const items = res?.data?.items || []
      // map to option { id, name }
      listSubject.value = items.map((item: any) => ({
        ...item,
        id: item.id,
        name: item.subject_name,
      }))
    }
    else {
      listSubject.value = []
    }
  }
  catch {
    listSubject.value = []
  }
}

watch(
  () => modelRef.value.class_block,
  async (val) => {
    if (!props.showSubject)
      return

    modelRef.value.subject_id = null
    await getListSubject(val)
  },
)

watch(
  () => props.showSubject,
  (val) => {
    const rules = rulesRef.value as any

    if (!val) {
      modelRef.value.subject_id = null
      listSubject.value = []
      rules.subject_id = []
      return
    }

    rules.subject_id = [
      {
        required: true,
        type: 'number',
        message: t('validation.required', { name: t('classroom.subject.subject') }),
        trigger: ['change'],
      },
    ]

    if (modelRef.value.class_block)
      getListSubject(modelRef.value.class_block)
  },
  { immediate: true },
)

onMounted(() => {
  getListClassBlock()
})
</script>

<template>
  <AppModal
    v-model:show="showModal"
    preset="card"
    transform-origin="center"
    :title="title"
    style="width: 600px"
    class="!rounded-2xl"
    content-class="!px-6 !py-0"
    header-class="!px-6 !pt-6 !pb-4 text-center"
    footer-class="!px-6 !pb-6 !pt-4 !w-full"
    @after-leave="handleCancel"
    @esc="handleCancel"
    @mask-click="handleCancel"
  >
    <div class="flex items-start gap-4">
      <div class="flex flex-col items-center gap-2 pt-1">
        <AppUpload
          :file-list="avatarFileList"
          accept="image/*"
          :max-file="1"
          :show-file-list="false"
          :show-upload-dragger="false"
          :show-remove-button="false"
          :show-preview-button="false"
          :loading="uploadingAvatar"
          @before-upload="onBeforeUploadAvatar"
          @update:file-list="onUpdateAvatarFileList"
        >
          <div class="relative w-24 h-24" :class="isView ? 'cursor-default' : 'cursor-pointer'">
            <div class="relative w-24 h-24 rounded-full overflow-hidden bg-purple-50 flex items-center justify-center">
              <template v-if="computedAvatarSrc">
                <img :src="computedAvatarSrc" alt="avatar" class="w-full h-full object-cover">
              </template>
              <template v-else>
                <SvgIcon icon="ion:image-outline" size="20" class="text-gray-500" />
              </template>
            </div>

            <div
              v-if="!isView"
              class="absolute -right-1 -bottom-1 z-30 w-8 h-8 rounded-full bg-primary flex items-center justify-center"
              style="border: 2px solid white;"
            >
              <SvgIcon icon="material-symbols:edit" size="14" class="text-white" />
            </div>
          </div>
        </AppUpload>
      </div>

      <NForm
        ref="formRef"
        :label-width="80"
        :model="modelRef"
        :rules="rulesRef"
        class="flex-1"
      >
        <NFormItem :label="$t('classroom.name')" path="name">
          <AppInput
            v-model:value="modelRef.name"
            :disabled="isView"
            :placeholder="t('classroom.namePlaceholder')"
            size="medium"
            class=""
          />
        </NFormItem>

        <NFormItem :label="$t('classroom.classBlock')" path="class_block">
          <AppSelect
            v-model:value="modelRef.class_block"
            :options="listClassBlock"
            :placeholder="t('classroom.classBlockPlaceholder')"
            label-field="name"
            value-field="code"
            :disabled="!props.isCreate"
            size="medium"
            class=""
          />
        </NFormItem>

        <NFormItem v-if="props.showSubject" :label="$t('classroom.subject.subject')" path="subject_id">
          <AppSelect
            v-model:value="modelRef.subject_id"
            :options="listSubject"
            :placeholder="t('classroom.examPaper.subjectPlaceholder')"
            label-field="name"
            value-field="id"
            :disabled="isView"
            size="medium"
            class=""
          />
        </NFormItem>

        <NFormItem :label="$t('classroom.descriptionClassroom')" path="description">
          <AppInput
            v-model:value="modelRef.description"
            :disabled="isView"
            :placeholder="t('classroom.descriptionPlaceholder')"
            :maxlength="255"
            size="medium"
          />
        </NFormItem>
      </NForm>
    </div>

    <template #footer>
      <div class="flex justify-end gap-4">
        <AppButton size="medium" class=" min-w-32" @click="handleCancel">
          {{ !isView ? $t('common.cancel') : $t('common.close') }}
        </AppButton>
        <AppButton v-if="!isView" size="medium" class=" min-w-32" type="primary" :loading="loading || uploadingAvatar" :disabled="uploadingAvatar" @click="handleSave">
          {{ props.showSubject ? $t('classroom.createAndSave') : $t('classroom.save') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<style scoped>
</style>
